// ============================================================
// CineSync — Subtitle Search & Manager Modal (VLSub Style)
// ============================================================

import { useState, useEffect } from 'react';
import {
  X,
  Search,
  Subtitles,
  Download,
  Check,
  Loader2,
  AlertCircle,
  Clock,
  User,
  Star,
  FileText,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { searchSubtitles, downloadSubtitle, parseSubtitleText } from '@/services/openSubtitlesService';
import { usePlayerStore } from '@/store/playerStore';
import { useRoomStore } from '@/store/roomStore';
import type { SubtitleSearchResult } from '@/types';

interface SubtitleSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const POPULAR_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'hi', name: 'Hindi' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ru', name: 'Russian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'tr', name: 'Turkish' },
  { code: 'pl', name: 'Polish' },
  { code: 'sv', name: 'Swedish' },
  { code: 'id', name: 'Indonesian' },
];

export function SubtitleSearchModal({ isOpen, onClose }: SubtitleSearchModalProps) {
  const { room } = useRoomStore();
  const {
    savedSubtitles,
    activeSubtitleId,
    subtitleEnabled,
    subtitleOffset,
    addSavedSubtitle,
    selectSavedSubtitle,
    setSubtitleEnabled,
    setSubtitleOffset,
    removeSavedSubtitle,
  } = usePlayerStore();

  const [activeTab, setActiveTab] = useState<'search' | 'manager'>('search');
  const [query, setQuery] = useState('');
  const [language, setLanguage] = useState('en');
  const [year, setYear] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [results, setResults] = useState<SubtitleSearchResult[]>([]);
  const [searchNotice, setSearchNotice] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [downloadSuccessMessage, setDownloadSuccessMessage] = useState<string | null>(null);

  // Auto-populate query from current room movie title
  useEffect(() => {
    if (isOpen && room?.movieTitle) {
      // Clean up common file extension suffix or video tags
      const cleanTitle = room.movieTitle
        .replace(/\.[^/.]+$/, '')
        .replace(/[._]/g, ' ')
        .replace(/\b(1080p|720p|4k|2160p|bluray|webrip|web-dl|x264|x265|hevc|aac)\b/gi, '')
        .trim();
      setQuery(cleanTitle);
    }
  }, [isOpen, room?.movieTitle]);

  if (!isOpen) return null;

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!query.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    setSearchNotice(null);

    try {
      const { results: items, notice } = await searchSubtitles({
        query: query.trim(),
        language,
        year: year ? parseInt(year, 10) : undefined,
      });

      setResults(items);
      setSearchNotice(notice || null);
      if (items.length === 0) {
        setSearchError('No matching subtitles found. Try refining your title or clearing the year.');
      }
    } catch (err: any) {
      setSearchError(err.message || 'Failed to search subtitles.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleDownloadAndLoad = async (item: SubtitleSearchResult) => {
    setDownloadingId(item.fileId);
    setSearchError(null);
    setDownloadSuccessMessage(null);

    try {
      const rawText = await downloadSubtitle(item.fileId, item.fileName);
      const parsedSub = parseSubtitleText(rawText, item.fileName, item.language);
      addSavedSubtitle(parsedSub);

      setDownloadSuccessMessage(`Loaded "${item.fileName}" successfully!`);
      setTimeout(() => setDownloadSuccessMessage(null), 4000);
    } catch (err: any) {
      setSearchError(err.message || 'Failed to download subtitle file.');
    } finally {
      setDownloadingId(null);
    }
  };

  const isYouTube = room?.mediaType === 'youtube';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in select-none contain-overscroll">
      <div className="relative w-full max-w-2xl bg-cinema-900 border border-cinema-700/80 rounded-xl shadow-cinema-screen text-cinema-100 flex flex-col max-h-[85vh] overflow-hidden contain-overscroll">
        {/* Header */}
        <div className="flex items-center justify-between p-5 hairline-b bg-cinema-950/60">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-cinema-800 border border-cinema-700 flex items-center justify-center text-lamp shadow-sm">
              <Subtitles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-cinema-100 tracking-tight flex items-center gap-2">
                <span>Subtitle Search</span>
                <span className="text-[10px] font-mono uppercase bg-cinema-800 text-cinema-400 border border-cinema-700 px-1.5 py-0.5 rounded">
                  OpenSubtitles
                </span>
              </h2>
              <p className="text-xs text-cinema-400">
                Search, download, and sync external subtitles in your screening room
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-cinema-400 hover:text-cinema-100 hover:bg-cinema-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* YouTube Notice Banner */}
        {isYouTube && (
          <div className="bg-amber-500/10 border-b border-amber-500/20 px-5 py-2.5 flex items-center gap-2.5 text-xs text-amber-300">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>Note:</strong> YouTube videos use YouTube's native closed captions [CC]. External subtitles from OpenSubtitles are active for local video streaming.
            </span>
          </div>
        )}

        {/* Tab Selector */}
        <div className="flex hairline-b bg-cinema-950/90 px-5 pt-3 gap-4 text-xs font-semibold">
          <button
            onClick={() => setActiveTab('search')}
            className={`pb-2.5 flex items-center gap-1.5 border-b-2 transition-colors ${
              activeTab === 'search'
                ? 'border-lamp text-cinema-100'
                : 'border-transparent text-cinema-400 hover:text-cinema-200'
            }`}
          >
            <Search className="w-3.5 h-3.5" />
            <span>Search OpenSubtitles</span>
          </button>

          <button
            onClick={() => setActiveTab('manager')}
            className={`pb-2.5 flex items-center gap-1.5 border-b-2 transition-colors ${
              activeTab === 'manager'
                ? 'border-lamp text-cinema-100'
                : 'border-transparent text-cinema-400 hover:text-cinema-200'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Loaded Subtitles ({savedSubtitles.length})</span>
          </button>
        </div>

        {/* Tab 1: Search OpenSubtitles */}
        {activeTab === 'search' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* Search Input Bar */}
            <form onSubmit={handleSearch} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
                {/* Title */}
                <div className="sm:col-span-7">
                  <label htmlFor="sub-search-title" className="block text-[10px] font-semibold uppercase tracking-wider text-cinema-400 mb-1">
                    Movie / Show Title
                  </label>
                  <div className="relative">
                    <input
                      id="sub-search-title"
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="e.g. Interstellar, Severance..."
                      className="w-full bg-cinema-950 border border-cinema-700 rounded-md px-3 py-2 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                    />
                  </div>
                </div>

                {/* Language */}
                <div className="sm:col-span-3">
                  <label htmlFor="sub-search-lang" className="block text-[10px] font-semibold uppercase tracking-wider text-cinema-400 mb-1">
                    Language
                  </label>
                  <select
                    id="sub-search-lang"
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="w-full bg-cinema-950 border border-cinema-700 rounded-md px-2.5 py-2 text-xs text-cinema-100 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors"
                  >
                    {POPULAR_LANGUAGES.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.name} ({l.code})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Year */}
                <div className="sm:col-span-2">
                  <label htmlFor="sub-search-year" className="block text-[10px] font-semibold uppercase tracking-wider text-cinema-400 mb-1">
                    Year (Opt.)
                  </label>
                  <input
                    id="sub-search-year"
                    type="text"
                    value={year}
                    onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="2024"
                    maxLength={4}
                    className="w-full bg-cinema-950 border border-cinema-700 rounded-md px-2.5 py-2 text-xs text-cinema-100 placeholder-cinema-500 focus:outline-none focus:border-lamp focus:ring-1 focus:ring-lamp transition-colors font-mono"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSearching || !query.trim()}
                className="w-full py-2.5 rounded-md bg-lamp hover:bg-lamp-bright disabled:opacity-40 disabled:cursor-not-allowed text-cinema-950 font-bold text-xs tracking-wide transition-all shadow flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
              >
                {isSearching ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Searching OpenSubtitles…</span>
                  </>
                ) : (
                  <>
                    <Search className="w-3.5 h-3.5" />
                    <span>Search Subtitles</span>
                  </>
                )}
              </button>
            </form>

            {/* Status Feedback */}
            {searchNotice && (
              <div className="p-2.5 rounded-md bg-cinema-950 border border-cinema-700/80 text-[11px] text-cinema-300 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-lamp flex-shrink-0" />
                <span>{searchNotice}</span>
              </div>
            )}

            {downloadSuccessMessage && (
              <div className="p-2.5 rounded-md bg-reel-emerald/10 border border-reel-emerald/20 text-xs text-reel-emerald flex items-center gap-2">
                <Check className="w-4 h-4 flex-shrink-0" />
                <span>{downloadSuccessMessage}</span>
              </div>
            )}

            {searchError && (
              <div className="p-2.5 rounded-md bg-red-500/10 border border-red-500/20 text-xs text-red-400 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{searchError}</span>
              </div>
            )}

            {/* Results List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-[11px] text-cinema-400 font-medium px-1">
                <span>Search Results ({results.length})</span>
                <span>Language: {language.toUpperCase()}</span>
              </div>

              {results.length === 0 && !isSearching && (
                <div className="text-center py-12 px-4 rounded-lg border border-dashed border-cinema-800 text-cinema-500 text-xs">
                  <Subtitles className="w-8 h-8 text-cinema-600 mx-auto mb-2" />
                  <p>Search for a title above to find subtitle tracks from OpenSubtitles.</p>
                </div>
              )}

              <div className="space-y-2">
                {results.map((item) => {
                  const isDownloading = downloadingId === item.fileId;
                  const isAlreadyLoaded = savedSubtitles.some((s) => s.name === item.fileName);

                  return (
                    <div
                      key={item.id}
                      className="p-3 rounded-lg bg-cinema-950/80 border border-cinema-750 hover:border-cinema-650 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-cinema-100 truncate max-w-sm sm:max-w-md" title={item.fileName}>
                            {item.fileName}
                          </span>
                          <span className="text-[10px] font-mono uppercase bg-cinema-800 text-lamp px-1.5 py-0.5 rounded border border-cinema-700">
                            {item.format.toUpperCase()}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 text-[11px] text-cinema-400 flex-wrap">
                          <span className="truncate max-w-[200px]" title={item.release}>
                            Release: {item.release}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {item.uploader}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Download className="w-3 h-3" />
                            {item.downloadCount} dl
                          </span>
                          {item.rating > 0 && (
                            <>
                              <span>•</span>
                              <span className="flex items-center gap-1 text-lamp">
                                <Star className="w-3 h-3 fill-current" />
                                {item.rating.toFixed(1)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <button
                        onClick={() => handleDownloadAndLoad(item)}
                        disabled={isDownloading}
                        className={`px-3 py-1.5 rounded text-xs font-semibold flex items-center justify-center gap-1.5 flex-shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp ${
                          isAlreadyLoaded
                            ? 'bg-reel-emerald/20 text-reel-emerald border border-reel-emerald/30'
                            : 'bg-cinema-800 hover:bg-cinema-750 border border-cinema-700 text-cinema-200 hover:text-cinema-100'
                        }`}
                      >
                        {isDownloading ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            <span>Loading…</span>
                          </>
                        ) : isAlreadyLoaded ? (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            <span>Active</span>
                          </>
                        ) : (
                          <>
                            <Download className="w-3.5 h-3.5 text-lamp" />
                            <span>Download & Load</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Loaded Subtitles Manager */}
        {activeTab === 'manager' && (
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {/* Global Subtitle Controls */}
            <div className="p-4 rounded-lg bg-cinema-950 border border-cinema-750 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-bold text-cinema-100 uppercase tracking-wider">
                    Subtitle Display
                  </h4>
                  <p className="text-[11px] text-cinema-400">
                    Enable or disable the onscreen subtitle overlay
                  </p>
                </div>

                <button
                  onClick={() => setSubtitleEnabled(!subtitleEnabled)}
                  className={`px-3 py-1.5 rounded text-xs font-bold transition-colors ${
                    subtitleEnabled
                      ? 'bg-lamp text-cinema-950 shadow-sm'
                      : 'bg-cinema-800 text-cinema-300 border border-cinema-700 hover:text-cinema-100'
                  }`}
                >
                  {subtitleEnabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>

              {/* Timing Offset */}
              <div className="pt-3 hairline-t flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-cinema-400" />
                  <span className="text-xs text-cinema-300">
                    Subtitle Timing Offset:
                  </span>
                  <span className="font-mono text-xs text-lamp font-bold">
                    {subtitleOffset === 0 ? '0.0s' : `${subtitleOffset > 0 ? '+' : ''}${subtitleOffset.toFixed(1)}s`}
                  </span>
                </div>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setSubtitleOffset(Math.round((subtitleOffset - 0.5) * 10) / 10)}
                    className="px-2.5 py-1 rounded bg-cinema-900 hover:bg-cinema-800 border border-cinema-700 text-xs text-cinema-300 hover:text-cinema-100"
                    title="Delay subtitles by 0.5s"
                  >
                    -0.5s
                  </button>
                  <button
                    onClick={() => setSubtitleOffset(0)}
                    className="px-2.5 py-1 rounded bg-cinema-900 hover:bg-cinema-800 border border-cinema-700 text-xs text-cinema-400 hover:text-cinema-100 flex items-center gap-1"
                    title="Reset offset"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>Reset</span>
                  </button>
                  <button
                    onClick={() => setSubtitleOffset(Math.round((subtitleOffset + 0.5) * 10) / 10)}
                    className="px-2.5 py-1 rounded bg-cinema-900 hover:bg-cinema-800 border border-cinema-700 text-xs text-cinema-300 hover:text-cinema-100"
                    title="Advance subtitles by 0.5s"
                  >
                    +0.5s
                  </button>
                </div>
              </div>
            </div>

            {/* Saved Subtitles List */}
            <div className="space-y-3">
              <h4 className="text-xs font-bold text-cinema-100 uppercase tracking-wider px-1">
                Downloaded & Local Subtitles ({savedSubtitles.length})
              </h4>

              {savedSubtitles.length === 0 ? (
                <div className="text-center py-10 rounded-lg border border-dashed border-cinema-800 text-cinema-500 text-xs">
                  No subtitles loaded yet. Search and download subtitles from OpenSubtitles above.
                </div>
              ) : (
                <div className="space-y-2">
                  {savedSubtitles.map((sub) => {
                    const isActive = activeSubtitleId === sub.id;

                    return (
                      <div
                        key={sub.id}
                        onClick={() => selectSavedSubtitle(sub.id)}
                        className={`p-3 rounded-lg border cursor-pointer transition-all flex items-center justify-between gap-3 ${
                          isActive
                            ? 'bg-lamp/10 border-lamp/60 text-cinema-100'
                            : 'bg-cinema-950/70 border-cinema-750 text-cinema-300 hover:border-cinema-600'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${
                            isActive ? 'bg-lamp text-cinema-950' : 'bg-cinema-800 text-cinema-400'
                          }`}>
                            {sub.language.substring(0, 2).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-cinema-100 truncate max-w-sm sm:max-w-md">
                              {sub.name}
                            </p>
                            <p className="text-[11px] text-cinema-400">
                              {sub.cues.length} dialogue cues
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {isActive && (
                            <span className="text-[11px] font-bold text-lamp px-2 py-0.5 rounded bg-lamp-dim border border-lamp/20 flex items-center gap-1">
                              <Check className="w-3 h-3" />
                              <span>Active</span>
                            </span>
                          )}

                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSavedSubtitle(sub.id);
                            }}
                            className="p-1.5 rounded text-cinema-400 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Remove subtitle"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-4 hairline-t bg-cinema-950/80 flex items-center justify-between text-xs text-cinema-400">
          <span>Powered by OpenSubtitles REST API</span>
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded bg-cinema-800 hover:bg-cinema-750 text-cinema-100 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-lamp"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

